import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAssumptions, useCalculations } from '../../hooks';
import { createCurrencyFormatter } from '../../utils';

// Framework
import { ReportLayout, SectionDivider } from './framework';

// Data
import { citations, scenarioPresets, ScenarioPreset } from './data';

// Sections
import {
  IntroSection,
  SituationSection,
  RealEstateSection,
  StockMarketSection,
  GeographySection,
  PropertySection,
  RentOptionSection,
  SellOptionSection,
  ComparisonSection,
  UncertaintySection,
  FrameworksSection,
  RecommendationSection,
  DecisionSection,
  SourcesSection,
  ReportMetrics,
} from './sections';

export const HouseDecisionReport: React.FC = () => {
  const [scenarioPreset, setScenarioPreset] = useState<ScenarioPreset>('balanced');
  const lastAppliedPreset = useRef<ScenarioPreset | null>(null);

  const { assumptions, updateAssumption } = useAssumptions();

  // Apply preset to assumptions
  useEffect(() => {
    if (lastAppliedPreset.current !== scenarioPreset) {
      const config = scenarioPresets[scenarioPreset];
      updateAssumption('propertyAppreciation', config.propertyAppreciation);
      updateAssumption('stockReturns', config.stockReturns);
      updateAssumption('rentGrowth', config.rentGrowth);
      lastAppliedPreset.current = scenarioPreset;
    }
  }, [scenarioPreset, updateAssumption]);

  const { results, finalYear, rentAdvantage } = useCalculations(assumptions);
  const formatCurrency = createCurrencyFormatter(false);

  // Calculate key metrics
  const metrics: ReportMetrics | null = useMemo(() => {
    if (!finalYear) return null;

    const saleProceeds = assumptions.propertyValue * (1 - assumptions.sellingCosts / 100);
    const year1Rent = assumptions.monthlyRent * 12;
    const year1Expenses = (assumptions.propertyValue * assumptions.propertyTaxRate / 100) +
      assumptions.insuranceCost +
      (assumptions.propertyValue * assumptions.maintenanceRate / 100) +
      (year1Rent * assumptions.vacancyRate / 100) +
      (year1Rent * (1 - assumptions.vacancyRate / 100) * assumptions.managementFee / 100);
    const year1NetIncome = year1Rent - year1Expenses;

    return {
      saleProceeds,
      year1Rent,
      year1Expenses,
      year1NetIncome,
      year10PropertyValue: finalYear.rentScenario.propertyValue,
      // Use net liquidation values for apples-to-apples comparison
      // Both represent "what you walk away with in cash after 10 years"
      year10RentValue: finalYear.rentScenario.netLiquidationValue,
      year10SellValue: finalYear.sellScenarios.portfolio80_20.netLiquidationValue,
      // Also expose gross values for reference
      year10RentGrossEquity: finalYear.rentScenario.totalEquityReal,
      year10SellGrossValue: finalYear.sellScenarios.portfolio80_20.totalValueReal,
      totalRentalIncome: results.reduce((sum, r) => sum + r.rentScenario.netRentalIncomeAfterTax, 0),
    };
  }, [assumptions, results, finalYear]);

  if (!metrics || !finalYear) {
    return <div className="p-8">Loading...</div>;
  }

  // Common props for sections
  const sectionProps = {
    assumptions,
    metrics,
    formatCurrency,
    citations,
  };

  const sectionWithResultsProps = {
    ...sectionProps,
    results,
    finalYear,
    rentAdvantage,
  };

  return (
    <ReportLayout
      title="House Decision Analysis"
      subtitle="2040 Beryl Lane, Newport Beach, CA"
      meta="Analysis prepared December 2025 · ~25 minute read"
    >
      <IntroSection {...sectionProps} />
      <SectionDivider />

      <SituationSection {...sectionProps} />
      <SectionDivider />

      <RealEstateSection citations={citations} />
      <SectionDivider />

      <StockMarketSection {...sectionProps} />
      <SectionDivider />

      <GeographySection citations={citations} />
      <SectionDivider />

      <PropertySection {...sectionProps} />
      <SectionDivider />

      <RentOptionSection {...sectionWithResultsProps} />
      <SectionDivider />

      <SellOptionSection {...sectionProps} />
      <SectionDivider />

      <ComparisonSection
        assumptions={assumptions}
        metrics={metrics}
        results={results}
        rentAdvantage={rentAdvantage}
        formatCurrency={formatCurrency}
        scenarioPreset={scenarioPreset}
        onScenarioChange={setScenarioPreset}
      />
      <SectionDivider />

      <UncertaintySection />
      <SectionDivider />

      <FrameworksSection {...sectionProps} />
      <SectionDivider />

      <RecommendationSection />
      <SectionDivider />

      <DecisionSection />
      <SectionDivider />

      <SourcesSection {...sectionProps} />
    </ReportLayout>
  );
};

export default HouseDecisionReport;
